import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import HeroSection from "./components/HeroSection";
import LinkCard from "./components/LinkCard";
import AboutSection from "./components/AboutSection";
import Background from "./components/Background";
import EntryGate from "./components/EntryGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultProfile = {
    name: "Kelltchup",
    bio: "Sua gótica mineira favorita 🖤",
    profile_image: "https://customer-assets.emergentagent.com/job_kelltchup-links/artifacts/ur7hsv43_C%C3%B3pia%20de%20C%C3%B3pia%20de%20IMG_8700.JPG.jpeg",
    links: [
        {
            id: "twitter",
            name: "X (Twitter)",
            url: "https://x.com/kelltchups2",
            icon: "twitter",
            highlight: false
        }
    ]
};

const preloadImage = (src) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
};

// Preserve any already-unlocked links when replacing profile from API
const mergeLinks = (apiLinks, existingLinks) => {
    const apiIds = new Set(apiLinks.map((l) => l.id));
    const extras = existingLinks.filter((l) => !apiIds.has(l.id));
    return [...extras, ...apiLinks];
};

function App() {
    const [profile, setProfile] = useState(defaultProfile);
    // Always start locked - gate shows on every visit
    const [unlocked, setUnlocked] = useState(false);

    useEffect(() => {
        preloadImage(defaultProfile.profile_image);

        const fetchProfile = async () => {
            try {
                const response = await axios.get(`${API}/profile`);
                setProfile((p) => ({ ...response.data, links: mergeLinks(response.data.links, p.links) }));
            } catch (e) {
                // silent fallback
            }
        };

        const fetchHiddenIfUnlocked = async () => {
            // No-op: gate always shows on visit. Hidden link is fetched on unlock.
        };

        fetchProfile();
        fetchHiddenIfUnlocked();
    }, []);

    const handleBackToGate = () => {
        setUnlocked(false);
        // Strip hidden adult links out so they're not lingering in DOM/memory while gate is shown again
        const hiddenIds = new Set(['privacy', 'fatalfans']);
        setProfile((p) => ({ ...p, links: p.links.filter((l) => !hiddenIds.has(l.id)) }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleUnlock = async () => {
        // Fetch hidden links ONLY after gate is unlocked (anti-crawler protection)
        try {
            const res = await axios.get(`${API}/unlock`);
            const hidden = res.data?.links || [];
            if (hidden.length > 0) {
                setProfile((p) => {
                    const hiddenIds = new Set(hidden.map((l) => l.id));
                    const remaining = p.links.filter((l) => !hiddenIds.has(l.id));
                    return { ...p, links: [...hidden, ...remaining] };
                });
            }
        } catch (e) {
            // silent fail
        }

        setUnlocked(true);
        axios.post(`${API}/click`, {
            link_name: 'Entry Gate Unlocked',
            link_url: 'entry_gate',
            user_agent: navigator.userAgent,
            referrer: document.referrer || null
        }).catch(() => {});
    };

    const handleLinkClick = async (link) => {
        axios.post(`${API}/click`, {
            link_name: link.name,
            link_url: link.url,
            user_agent: navigator.userAgent,
            referrer: document.referrer || null
        }).catch(() => {});
    };

    return (
        <div className="min-h-screen relative bg-[#0a0608]" data-testid="app-container">
            <Background />

            <div className="noise-overlay" />

            <AnimatePresence>
                {!unlocked && (
                    <EntryGate
                        coverImage={`${API}/img/cover`}
                        onUnlock={handleUnlock}
                    />
                )}
            </AnimatePresence>

            <Toaster
                position="top-center"
                toastOptions={{
                    style: {
                        background: 'rgba(15, 8, 10, 0.9)',
                        border: '1px solid rgba(251, 113, 133, 0.3)',
                        color: '#fff',
                        backdropFilter: 'blur(12px)'
                    }
                }}
            />

            <main className="relative z-10 max-w-md mx-auto min-h-screen pb-10">
                {/* Hero with rectangular photo */}
                <HeroSection profile={profile} />

                {/* Primary Links - first fold */}
                <div className="px-5 pt-5 space-y-3" data-testid="links-section">
                    {profile.links.map((link, index) => (
                        <LinkCard
                            key={link.id}
                            link={link}
                            index={index}
                            onLinkClick={handleLinkClick}
                        />
                    ))}
                </div>

                {/* About / Personality cards */}
                <AboutSection />

                {/* Footer */}
                <footer
                    className="pt-8 pb-6 text-center text-rose-200/40 text-xs space-y-4"
                    data-testid="footer"
                >
                    <button
                        type="button"
                        onClick={handleBackToGate}
                        data-testid="back-to-gate-btn"
                        className="inline-flex items-center gap-1.5 text-rose-300/80 hover:text-rose-200 text-sm font-medium tracking-wide transition-colors group"
                    >
                        <span className="inline-block transition-transform group-hover:-translate-x-0.5">←</span>
                        voltar para o início
                    </button>
                    <p data-testid="footer-text">
                        © 2025 Kelltchup • feito com <span className="text-rose-400">❤</span> em SP
                    </p>
                </footer>
            </main>
        </div>
    );
}

export default App;
